import {useRef, type ReactNode} from 'react';
import {Navigate} from 'react-router-dom';
import {useAppContext} from '@/matic/providers/AppProvider';
import {consumeReturnTo} from '@/matic/lib/auth/post-login-redirect';
import {
  useQuotesFirst,
  useQuotingAvailability,
} from '@/matic/features/quoting/hooks/useQuotingAvailability';

type Props = {
  /** What `/` shows on the AES quotes-first build: the Quote Desk home, rendered in place. */
  home: ReactNode;
};

/**
 * Index route component, inside the protected layout.
 *
 * On the AES quotes-first build `/` *is* the Quote Desk home, rendered here. Other quoting
 * tenants land on `/quotes`; everyone else on `/home`.
 * If `AuthGuard` stashed a deep link the user
 * was trying to reach before bouncing through login (see
 * `lib/auth/post-login-redirect.ts`), they are sent there instead.
 *
 * Why we wait on `isLoading`:
 *
 * Cognito's redirect_uri is `${origin}/` (see lib/auth/user-manager.ts), so
 * after a successful sign-in the browser lands on `/?code=…&state=…`. The
 * persistent #app-loader in index.html is still covering the screen at this
 * point. If we render `<Navigate replace />` here unconditionally, Navigate's
 * effect (child) fires before react-oidc-context's AuthProvider effect
 * (ancestor), `history.replaceState` strips the `code`/`state` query params,
 * signinCallback() never runs, and the user gets bounced back through
 * /login → Cognito → / in an infinite boot loop.
 *
 * Returning `null` while `isLoading` keeps the URL untouched so the OIDC
 * callback can complete, and the persistent #app-loader continues to cover
 * the screen — no flicker.
 */
export function IndexRedirect({home}: Props) {
  const {isLoading} = useAppContext();
  const {loading, enabled} = useQuotingAvailability();
  const quotesFirst = useQuotesFirst();
  // Ref (not state) so sessionStorage is only ever drained once per mount,
  // even across a dev-mode StrictMode double-render.
  const returnToRef = useRef<string | undefined | null>(undefined);

  if (isLoading) {
    return null;
  }

  if (returnToRef.current === undefined) {
    returnToRef.current = consumeReturnTo() ?? null;
  }

  if (returnToRef.current && returnToRef.current !== '/') {
    return <Navigate to={returnToRef.current} replace />;
  }

  // Temporary AES split (see SidebarLayout): consolidate onto `/quotes` with the rest.
  if (loading || quotesFirst === undefined) return null;
  if (quotesFirst) return <>{home}</>;
  if (enabled) return <Navigate to="/quotes" replace />;
  return <Navigate to="/home" replace />;
}
